import { Text, useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import classNames from 'classnames/bind';
import React from 'react';
import Link from 'components/helpers/Link/Link';
import { useI18n } from 'next-localization';

import compose from 'lib/enhancers/compose';
import styles from './PlanDocuments.module.scss';
import pdStyles from 'components/PlanData/PlanDetails/PlanDetails.module.scss';
import { DocumentTargetItem } from 'components/PlanData/Types/GetPlanDetailsResponse.Type';

type PlanDocumentsProps = {
  documents: DocumentTargetItem[];
};
const formatDate = (dateString: string) => {
  const year = Number(dateString.substring(0, 4));
  const month = Number(dateString.substring(4, 6));
  const day = Number(dateString.substring(6, 8));

  return `${month}/${day}/${year}`;
};

// Function to sort documents by language
const sortDocumentsByLanguage = (documents: DocumentTargetItem[]) => {
  // Create an object to hold documents for each language
  const documentsByLanguage: { [key: string]: DocumentTargetItem[] } = {};

  // Group documents by language
  documents.forEach((item) => {
    const language = item.documentLanguage.targetItem.value.value as string;
    if (!documentsByLanguage[language]) {
      documentsByLanguage[language] = [];
    }
    documentsByLanguage[language].push(item);
  });

  return documentsByLanguage;
};

const cx = classNames.bind(styles);
const cxPlanDetail = classNames.bind(pdStyles);

const PlanDocuments = (props: PlanDocumentsProps): JSX.Element => {
  const { t } = useI18n();

  const documentsByLanguage = sortDocumentsByLanguage(props.documents);
  const numberOfLanguages = Object.keys(documentsByLanguage).length;
  /** Determines if the link component is being viewed in Experience Editor. */
  const { sitecoreContext } = useSitecoreContext();
  const docLinkClass = sitecoreContext.pageEditing ? styles['doc-link-editing'] : styles['doc-link'];

  return (
    <div className={cx('plan-documents')}>
      <h2 className={cxPlanDetail('section-header', 'container-edge')}>Plan Documents</h2>
      {Object.entries(documentsByLanguage).map(
        ([language, documents]) =>
          documents.length > 0 && (
            <div key={language}>
              {numberOfLanguages > 1 && <h3>{language}</h3>}
              {documents.map((item: DocumentTargetItem, index: number) => (
                <div key={index} className={cx('document-item')}>
                  <div className={docLinkClass}>
                    {item.documentLanguage.targetItem.languageAttribute.value?.toString() ? (
                      <Link
                        field={item.documentLink.jsonValue}
                        icon="external-link"
                        showLinkTextWithChildrenPresent={false}
                        target={item.documentLink?.jsonValue.value?.target || '_blank'}
                        lang={item.documentLanguage.targetItem.languageAttribute.value?.toString()}
                      >
                        <Text field={item.documentTitle} encode={false} />
                        <span aria-label={t('Aria-PDF-New-Window')}>&nbsp;(PDF)</span>
                      </Link>
                    ) : (
                      <Link
                        field={item.documentLink.jsonValue}
                        icon="external-link"
                        showLinkTextWithChildrenPresent={false}
                        target={item.documentLink?.jsonValue.value.target || '_blank'}
                      >
                        <Text field={item.documentTitle} encode={false} />
                        <span aria-label={t('Aria-PDF-New-Window')}>&nbsp;(PDF)</span>
                      </Link>
                    )}
                  </div>
                  <Text
                    tag="p"
                    className={cx('updated-date')}
                    field={{
                      value: item.updatedDate.value
                        ? item.documentLanguage.targetItem.lastUpdatedDateLabel.value +
                          ' ' +
                          formatDate(item.updatedDate.value)
                        : '',
                    }}
                  />
                </div>
              ))}
            </div>
          )
      )}
    </div>
  );
};

export default compose<PlanDocumentsProps>()(PlanDocuments);
